/**
 * ConnectionTab - Compact connection management UI
 *
 * A sleek, compact component that shows:
 * - Current connection status with left border accent
 * - Quick actions (test)
 * - Inline connection picker
 */

import { useState, useMemo, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Input,
  Spinner,
  Badge,
  mergeClasses,
} from '@fluentui/react-components';
import { useQueryClient } from '@tanstack/react-query';
import {
  CheckmarkCircle16Filled,
  DismissCircle16Filled,
  Warning16Filled,
  PlugDisconnected16Regular,
  Search16Regular,
  Add16Regular,
  ArrowSync16Regular,
  Database20Regular,
} from '@fluentui/react-icons';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useNodeConnectionStatus } from '@/hooks/useNodeConnectionStatus';
import { isConnectionReferenceComplete } from '@/services/connectionService';
import { useTestConnection, useConnections, useRegisterCreatedConnection, connectionKeys } from '@/hooks/useConnectionMutations';
import { useNodeOperationInfo } from '@/hooks/useNodeOperationInfo';
import { useOperationManifest } from '@/hooks/useOperationManifest';
import { useServiceProviderConnector } from '@/hooks/useServiceProviderConnector';
import { useGetConnectorInfo } from '@/contexts';
import { actionNameKey } from '@/hooks/useWorkflowConnectors';
import { extractConnectorName, isAgentConnectionId, isManagedApiConnector, getValidConnections, resolveCreationConnectorName, mapApiConnectionsToMerged, classifyMergedConnectionType, filterConnectionsBySearch, type MergedConnection } from '@/utils/connections';
import { FOUNDRY_AGENT_PROVIDERS, getAgentReferenceProvider, getAllowedAgentProviders, readAgentModelType, readAgentHarnessType, type AgentModelProvider } from '../../workflowHelpers';
import { buildConnectorManifest } from '@/services/manifestConnectionService';
import { ConnectionCreationPanel } from '../dialogs/ConnectionCreationPanel';
import { CompactConnectionItem } from '@/components/common/CompactConnectionItem';
import type { ConnectionReference} from '@/types/connectionReference';
import type { AgentModelType } from '@/types/agentConnection';
import { getAgentVariantFromTypes } from '../../helpers/agentVariant';

/**
 * Map an AgentModelProvider (connection-level) to the corresponding AgentModelType
 * (action parameter) for the *live UI state*. Most values are identical, but
 * 'OpenAI' connections use 'V1ChatCompletionsService'. (GitHubCopilot stays as-is
 * here — the serializer rewrites it to V1ChatCompletionsService at save time.)
 */
const providerToModelType = (provider: AgentModelProvider): AgentModelType => {
  if (provider === 'OpenAI') return 'V1ChatCompletionsService';
  return provider as AgentModelType;
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    flex: 1,
    minHeight: 0,
  },

  // Current connection card - compact with left border accent
  currentCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke2,
  },
  currentCardConnected: {
    borderLeftColor: tokens.colorPaletteGreenBorder1,
  },
  currentCardError: {
    borderLeftColor: tokens.colorPaletteRedBorder1,
  },
  currentCardWarning: {
    borderLeftColor: tokens.colorPaletteYellowBorder1,
  },
  currentCardMock: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
    borderTopColor: tokens.colorPaletteBlueBorderActive,
    borderRightColor: tokens.colorPaletteBlueBorderActive,
    borderBottomColor: tokens.colorPaletteBlueBorderActive,
    borderLeftColor: tokens.colorPaletteBlueBorderActive,
  },

  // Card header - compact, no colored background
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },

  statusIcon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  statusIconConnected: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusIconError: {
    color: tokens.colorPaletteRedForeground1,
  },
  statusIconWarning: {
    color: tokens.colorPaletteYellowForeground1,
  },
  statusIconNone: {
    color: tokens.colorNeutralForeground3,
  },
  statusIconMock: {
    color: tokens.colorPaletteBlueForeground2,
  },

  statusInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  statusTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusSubtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  // Actions inline with header
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },

  // Connection list section
  listSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  listTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Search
  searchInput: {
    width: '100%',
  },

  // Connection items
  connectionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
    overflowY: 'auto',
  },

  // Create new button - compact single line
  createNewButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'background-color, border-color',
    transitionDuration: '0.15s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
      border: `1px dashed ${tokens.colorBrandStroke1}`,
    },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '-2px',
    },
  },
  createNewIcon: {
    width: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorBrandBackground2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
  },
  createNewText: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },

  // Empty state - compact
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
    gap: tokens.spacingVerticalS,
  },
  emptyIcon: {
    fontSize: '24px',
    color: tokens.colorNeutralForeground4,
  },
  emptyText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  // Loading
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalM,
  },
});

interface ConnectionTabProps {
  nodeId?: string | undefined;
  nodeType?: string | undefined;
  connectionName?: string | undefined;
}

export const ConnectionTab = ({
  nodeId: propNodeId,
  nodeType: propNodeType,
  connectionName: propConnectionName,
}: ConnectionTabProps) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreationPanel, setShowCreationPanel] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);

  // Get state from stores
  const { node, parameterValues, setConnection, setSlotConnection, setParameter } = useNodeEditorStore();
  const setNodeConnection = useConnectionStore((state) => state.setNodeConnection);
  const replaceConnectionKey = useConnectionStore((state) => state.replaceConnectionKey);
  const registerCreatedConnection = useRegisterCreatedConnection();
  const connectionReferences = useConnectionStore((state) => state.references);

  // Fetch live connections from data-plane API
  const { data: apiConnections, isLoading: isLoadingConnections } = useConnections();
  const validApiConnections = useMemo(() => getValidConnections(apiConnections ?? []), [apiConnections]);

  // Derive node info
  const nodeId = propNodeId ?? node?.id ?? '';
  const nodeType = propNodeType ?? node?.nodeData?.type;
  const isAgentOperation = nodeType === 'Agent' || nodeType === 'AgentLoop';

  // Current connection
  const currentConnectionName = propConnectionName ?? (
    isAgentOperation
      ? (parameterValues['_modelConnection_model1'] as string | undefined)
      : (parameterValues['_connection'] as string | undefined)
  );

  // Connection status
  const { validationResult, hasConnection, connectionName: resolvedName } = useNodeConnectionStatus({
    nodeId,
    operationType: nodeType ?? '',
    connectionReferenceKey: currentConnectionName,
  });

  // Test mutation
  const testMutation = useTestConnection();

  // Connection status helpers
  const getStatusInfo = () => {
    if (!currentConnectionName) {
      return {
        status: 'none' as const,
        title: 'No connection',
        subtitle: 'Select one below',
        icon: <PlugDisconnected16Regular className={mergeClasses(styles.statusIcon, styles.statusIconNone)} />,
      };
    }

    // Check for mock-active status first (template mode with mocks enabled)
    if (validationResult?.status === 'mock-active') {
      return {
        status: 'mock' as const,
        title: 'Using Test Data',
        subtitle: 'Connection not configured',
        icon: <Database20Regular className={mergeClasses(styles.statusIcon, styles.statusIconMock)} />,
      };
    }

    if (validationResult?.isValid || hasConnection) {
      return {
        status: 'connected' as const,
        title: resolvedName ?? currentConnectionName,
        subtitle: 'Connected',
        icon: <CheckmarkCircle16Filled className={mergeClasses(styles.statusIcon, styles.statusIconConnected)} />,
      };
    }

    if (validationResult?.status === 'missing') {
      return {
        status: 'error' as const,
        title: currentConnectionName,
        subtitle: 'Not found',
        icon: <DismissCircle16Filled className={mergeClasses(styles.statusIcon, styles.statusIconError)} />,
      };
    }

    return {
      status: 'warning' as const,
      title: currentConnectionName,
      subtitle: 'Needs attention',
      icon: <Warning16Filled className={mergeClasses(styles.statusIcon, styles.statusIconWarning)} />,
    };
  };

  const statusInfo = getStatusInfo();

  // Resolve connector ID using the centralized hook (handles all node types and fallbacks)
  const { apiId: resolvedApiId, operationInfo } = useNodeOperationInfo({
    nodeData: node?.nodeData,
    parameterValues,
  });
  
  // Get connector ID for non-agent operations
  // resolvedApiId covers managed APIs (from host.apiId / connection refs)
  // operationInfo.connectorId covers service providers (from serviceProviderConfiguration)
  const connectorId = isAgentOperation ? undefined : (resolvedApiId ?? operationInfo?.connectorId);

  // Fetch operation manifest to extract connector metadata (needed for service provider connection creation)
  const { data: operationManifest } = useOperationManifest(operationInfo);

  // Fetch the full standalone connector definition. `buildConnectorManifest`
  // prefers this over the trimmed `manifest.connector` embedded in the
  // operation manifest (which drops auth-provider parameter sets).
  const { data: serviceProviderConnector } = useServiceProviderConnector(connectorId ?? null);

  const connectorManifest = useMemo(
    () =>
      buildConnectorManifest({
        connectorId,
        serviceProviderConnector,
        operationManifest,
      }),
    [connectorId, serviceProviderConnector, operationManifest]
  );

  // Get connector display info (icon, name). Lookup order:
  //   1. By managed-API name extracted from connectorId
  //   2. By the active connection reference key
  //   3. By the node's namespaced action name — useWorkflowConnectors keys the
  //      map by `action:<name>` too, so connection-less nodes resolve their
  //      connector icon / display name even though display fields aren't
  //      persisted on the action.
  const getConnectorInfo = useGetConnectorInfo();
  const apiNameForLookup = connectorId?.match(/managedApis\/([^/]+)$/)?.[1];
  const connectorInfo =
    getConnectorInfo(apiNameForLookup ?? '') ||
    getConnectorInfo(currentConnectionName ?? '') ||
    getConnectorInfo(actionNameKey(node?.name ?? ''));

  // Extract the connector name from the resolved API ID for filtering.
  // Prefer the operation manifest's connector id when available, since the
  // workflow-stored host.apiId can be a legacy/malformed value
  // (e.g. "/automationProjects/foo/managedApis/" with no connector tail).
  const resolvedConnectorName = useMemo(() => {
    const manifestConnectorId = operationManifest?.properties?.connector?.id;
    const manifestName = manifestConnectorId ? extractConnectorName(manifestConnectorId) : '';
    if (manifestName) return manifestName;
    if (!connectorId) return undefined;
    const fromConnectorId = extractConnectorName(connectorId);
    return fromConnectorId || undefined;
  }, [connectorId, operationManifest]);

  // Determine expected connection type from the node's operation type
  const isServiceProviderOperation = nodeType === 'ServiceProvider';

  // Agent model type — derived before `filteredConnections` so the agent-variant
  // filter can use it. Defaults to 'AzureOpenAI' since the downstream creation
  // panel reads this value to pick the right auth form for new connections.
  const agentModelType = useMemo(() => {
    if (!isAgentOperation) return undefined;
    const inputs = (node?.nodeData as Record<string, unknown> | undefined)?.inputs as
      | Record<string, unknown>
      | undefined;
    return readAgentModelType(parameterValues, inputs) ?? 'AzureOpenAI';
  }, [isAgentOperation, parameterValues, node]);

  // Derive the agent variant (native/foundry/coding) using the same helpers
  // the rest of the codebase uses. The variant determines whether the provider
  // selector (GHCP OAuth option) appears in the connection creation panel.
  const agentVariant = useMemo(() => {
    if (!isAgentOperation) return undefined;
    const inputs = (node?.nodeData as Record<string, unknown> | undefined)?.inputs as
      | Record<string, unknown>
      | undefined;
    const harnessType = readAgentHarnessType(parameterValues, inputs);
    return getAgentVariantFromTypes(agentModelType, harnessType);
  }, [isAgentOperation, parameterValues, node, agentModelType]);

  // Map of agent connection key/id → provider, derived from workflow-scoped
  // references. Used both to filter the list by variant and to tint each item's
  // icon (teal = native, purple = Foundry).
  const agentProviderByKey = useMemo(() => {
    const map = new Map<string, AgentModelProvider | undefined>();
    for (const [refKey, ref] of Object.entries(connectionReferences)) {
      if (!isAgentConnectionId(ref.api?.id)) continue;
      const provider = getAgentReferenceProvider(ref);
      map.set(refKey, provider);
      const connId = ref.connection?.id;
      if (connId) map.set(connId, provider);
    }
    return map;
  }, [connectionReferences]);

  // Build connection list from data-plane API + locally-created connections from the store
  const filteredConnections = useMemo(() => {
    // Merge live data-plane connections with locally-created store references
    // that aren't in the API response yet. The base API mapping is shared with
    // the template picker via mapApiConnectionsToMerged.
    let connections: MergedConnection[] = mapApiConnectionsToMerged(
      validApiConnections,
      getConnectorInfo,
    );

    // Merge locally-created connections from the store that aren't in the API response yet
    // (e.g., service provider connections created in-memory before the draft save propagates)
    const apiKeys = new Set(connections.map((c) => c.key));
    // Dedup by underlying connection.id too: workflow references commonly use a
    // friendly alias (e.g. `serviceBus`) that differs from the ARM resource
    // name returned by the data-plane (e.g. `servicebus-1`), so a key-only
    // check would surface the same connection twice.
    const apiConnectionIds = new Set(
      connections
        .map((c) => c.connectionId?.toLowerCase())
        .filter((id): id is string => !!id),
    );
    // Precompute the case-insensitive set of available connection IDs so the
    // per-reference existence check below is O(1) instead of scanning the full
    // connection list on every iteration.
    const availableConnIds = new Set(
      (validApiConnections ?? []).map((c) => c.id.toLowerCase()),
    );
    for (const [refKey, ref] of Object.entries(connectionReferences)) {
      if (apiKeys.has(refKey)) continue;
      const refConnectionId = (ref.connection?.id ?? '').toLowerCase();
      if (refConnectionId && apiConnectionIds.has(refConnectionId)) continue;
      const apiId = ref.api?.id ?? '';
      const connType = classifyMergedConnectionType(apiId);

      const hasBackingConnection = isManagedApiConnector(apiId)
        ? availableConnIds.has(refConnectionId)
        // Service-provider and agent references have no ARM-backed connection
        // resource — `extractConnectionReferences` synthesizes
        // `connection.id = refKey` for them. That means a template-applied
        // placeholder (no `parameterValues` / `authentication`) would otherwise
        // pass through this gate and render as a valid pickable connection in
        // the SP/agent list. Require the reference to actually carry
        // configuration before we surface it.
        : isConnectionReferenceComplete(ref);

      if (hasBackingConnection) {
        const apiName = extractConnectorName(apiId);
        const info = getConnectorInfo(refKey) ?? getConnectorInfo(apiName);
        connections.push({
          key: refKey,
          name: ref.connectionName ?? refKey,
          type: connType,
          apiId,
          ...(ref.connection?.id ? { connectionId: ref.connection.id } : {}),
          ...(info?.iconUri ? { iconUri: info.iconUri } : {}),
        });
      }
    }

    if (import.meta.env.DEV && !resolvedConnectorName && !isAgentOperation && !isServiceProviderOperation) {
      console.warn(
        '[ConnectionTab] cannot determine connector name for managed-API node — hiding connection list to avoid surfacing unrelated connections',
        {
          nodeType,
          connectorId,
          operationManifestConnectorId: operationManifest?.properties?.connector?.id,
        },
      );
    }

    // 1. Always filter by connection type (agent / serviceProvider / managedApi)
    if (isAgentOperation) {
      // Mirror AgentTypeView: keep only agent connections whose provider
      // matches the agent's variant (coding / foundry / native). Classification
      // reads `connectionProperties` from the workflow's connection-store
      // references (the data-plane payload strips this). When no reference
      // exists for a connection (data-plane-only entry) we leave it visible so
      // users can still discover unmanaged connections.
      const allowed = getAllowedAgentProviders(agentVariant ?? 'native');
      connections = connections.filter((c) => {
        if (c.type !== 'agent') return false;
        const provider = agentProviderByKey.get(c.key) ?? (c.connectionId ? agentProviderByKey.get(c.connectionId) : undefined);
        if (provider === undefined) return true;
        return allowed.has(provider);
      });
    } else if (isServiceProviderOperation) {
      connections = connections.filter((c) => c.type === 'serviceProvider');
    } else {
      connections = connections.filter((c) => c.type === 'managedApi');
    }

    // 2. Further narrow by specific connector name when available.
    // For managed-API operations we *require* a connector name — otherwise we
    // would silently show every managed connection in the app (unrelated to
    // the current operation). When the name can't be determined (e.g. the
    // node's host.apiId is malformed and the operation manifest hasn't loaded)
    // we return an empty list so the user is prompted to create a connection
    // rather than picking a wrong one.
    if (resolvedConnectorName) {
      connections = connections.filter((c) => {
        const name = extractConnectorName(c.apiId);
        return name.toLowerCase() === resolvedConnectorName.toLowerCase();
      });
    } else if (!isAgentOperation && !isServiceProviderOperation) {
      connections = [];
    }

    if (searchQuery) {
      connections = filterConnectionsBySearch(connections, searchQuery);
    }

    return connections;
  }, [validApiConnections, connectionReferences, searchQuery, isAgentOperation, isServiceProviderOperation, resolvedConnectorName, getConnectorInfo, nodeType, connectorId, operationManifest, agentVariant, agentProviderByKey]);

  // Handlers
  const handleSelectConnection = useCallback((referenceKey: string) => {
    if (isAgentOperation) {
      setSlotConnection('model1', referenceKey);
      // Sync agentModelType to match the selected connection's provider so the
      // workflow JSON stays consistent with the actual model endpoint used at runtime.
      const ref = connectionReferences[referenceKey];
      const provider = getAgentReferenceProvider(ref);
      if (provider) {
        const modelType = providerToModelType(provider);
        if (modelType !== agentModelType) {
          setParameter('agentModelType', modelType);
        }
      }
    } else {
      setConnection(referenceKey);
    }
    if (nodeId) {
      setNodeConnection(nodeId, referenceKey);
    }
    setTestResult(null);
  }, [isAgentOperation, setSlotConnection, setConnection, nodeId, setNodeConnection, connectionReferences, agentModelType, setParameter]);

  const handleTestConnection = useCallback(async () => {
    if (!currentConnectionName) return;
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync(currentConnectionName);
      setTestResult(result.status === 'connected' ? 'success' : 'error');
    } catch {
      setTestResult('error');
    }
  }, [currentConnectionName, testMutation]);

  const handleCreateComplete = useCallback((refKey: string, ref: ConnectionReference) => {
    const sourceRefKey = currentConnectionName;
    const existingSourceRef = sourceRefKey ? connectionReferences[sourceRefKey] : undefined;
    const sourceIsPlaceholder =
      !!sourceRefKey &&
      !!existingSourceRef &&
      !isConnectionReferenceComplete(existingSourceRef);

    if (sourceIsPlaceholder && sourceRefKey) {
      // Template placeholders should be replaced atomically so they do not
      // remain alongside the newly-created connection in connections.json.
      replaceConnectionKey(sourceRefKey, refKey, ref);
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() });
    } else {
      registerCreatedConnection(refKey, ref);
    }

    handleSelectConnection(refKey);
    // Sync agentModelType when the created connection's provider differs from
    // the current value (e.g. user picked GitHubCopilot in the provider selector
    // but the node was seeded with 'AzureOpenAI' at creation time).
    if (isAgentOperation) {
      const provider = getAgentReferenceProvider(ref);
      if (provider) {
        const modelType = providerToModelType(provider);
        if (modelType !== agentModelType) {
          setParameter('agentModelType', modelType);
        }
      }
    }
    setShowCreationPanel(false);
  }, [currentConnectionName, connectionReferences, replaceConnectionKey, queryClient, registerCreatedConnection, handleSelectConnection, isAgentOperation, agentModelType, setParameter]);

  // Connector display info now comes entirely from the connector manifest via
  // useWorkflowConnectors — nothing is persisted on the node metadata.
  const connectorIcon = connectorInfo?.iconUri;

  // Render creation panel
  if (showCreationPanel) {
    const creationConnectorName = resolveCreationConnectorName({
      isAgentOperation,
      agentModelType,
      connectorDisplayName: connectorInfo?.displayName,
      nodeType,
    });

    return (
      <div className={styles.container}>
        <ConnectionCreationPanel
          {...(connectorId ? { connectorId } : {})}
          connectorType={nodeType}
          {...(creationConnectorName ? { connectorName: creationConnectorName } : {})}
          {...(connectorIcon ? { connectorIcon } : {})}
          {...(connectorManifest ? { connectorManifest } : {})}
          {...(agentModelType ? { agentModelType: agentModelType as AgentModelType } : {})}
          {...(agentVariant ? { agentVariant } : {})}
          onComplete={handleCreateComplete}
          onBack={() => setShowCreationPanel(false)}
        />
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="connection-tab">
      {/* Current Connection Card - Compact */}
      <div
        className={mergeClasses(
          styles.currentCard,
          statusInfo.status === 'connected' && styles.currentCardConnected,
          statusInfo.status === 'error' && styles.currentCardError,
          statusInfo.status === 'warning' && styles.currentCardWarning,
          statusInfo.status === 'mock' && styles.currentCardMock
        )}
        data-testid="connection-status-card"
      >
        <div className={styles.cardHeader}>
          {statusInfo.icon}
          <div className={styles.statusInfo}>
            <Text className={styles.statusTitle}>{statusInfo.title}</Text>
            <Text className={styles.statusSubtitle}>{statusInfo.subtitle}</Text>
          </div>
          {currentConnectionName && statusInfo.status !== 'mock' && (
            <div className={styles.headerActions}>
              <Button
                appearance="subtle"
                size="small"
                icon={testMutation.isPending ? <Spinner size="extra-tiny" /> : <ArrowSync16Regular />}
                onClick={handleTestConnection}
                disabled={testMutation.isPending}
                aria-label={testResult === 'success' ? 'Test passed' : testResult === 'error' ? 'Test failed' : 'Test connection'}
              />
            </div>
          )}
        </div>
      </div>

      {/* Connection List */}
      <div className={styles.listSection}>
        <div className={styles.listHeader}>
          <Text className={styles.listTitle}>
            Connections
          </Text>
          {filteredConnections.length > 0 && (
            <Badge appearance="tint" color="informative" size="small">
              {filteredConnections.length}
            </Badge>
          )}
        </div>

        <Input
          className={styles.searchInput}
          contentBefore={<Search16Regular />}
          placeholder="Search..."
          value={searchQuery}
          onChange={(_, data) => setSearchQuery(data.value)}
          appearance="filled-darker"
          size="small"
          data-testid="connection-search-input"
        />

        {/* Create new - compact */}
        <div
          className={styles.createNewButton}
          onClick={() => setShowCreationPanel(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setShowCreationPanel(true)}
          aria-label="Create new connection"
          data-testid="create-new-connection-button"
        >
          <div className={styles.createNewIcon}>
            <Add16Regular />
          </div>
          <Text className={styles.createNewText}>
            Create new connection
          </Text>
        </div>

        {/* List */}
        {isLoadingConnections ? (
          <div className={styles.loadingContainer}>
            <Spinner size="tiny" />
          </div>
        ) : filteredConnections.length === 0 ? (
          <div className={styles.emptyState}>
            <PlugDisconnected16Regular className={styles.emptyIcon} />
            <Text className={styles.emptyText}>No connections found</Text>
          </div>
        ) : (
          <div className={styles.connectionList} role="listbox" aria-label="Available connections">
            {filteredConnections.map((conn) => {
              const provider = conn.type === 'agent'
                ? agentProviderByKey.get(conn.key) ?? (conn.connectionId ? agentProviderByKey.get(conn.connectionId) : undefined)
                : undefined;
              const aiVariant = provider && FOUNDRY_AGENT_PROVIDERS.has(provider) ? 'foundry' : 'native';
              return (
                <CompactConnectionItem
                  key={conn.key}
                  name={conn.name}
                  referenceKey={conn.key}
                  {...(conn.iconUri ? { iconUri: conn.iconUri } : {})}
                  isAI={isAgentOperation}
                  aiVariant={aiVariant}
                  status="connected"
                  isSelected={currentConnectionName === conn.key}
                  onSelect={handleSelectConnection}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

ConnectionTab.displayName = 'ConnectionTab';
